import { useQuery } from '@tanstack/react-query'
import { CalendarClock, CalendarSearch, RefreshCw, Sprout, Timer, Zap } from 'lucide-react'
import { convictionApi } from '../api/client'
import { useNow } from '../hooks/useNow'
import {
  addBusinessDays,
  daysUntil,
  etTodayISO,
  etWallClock,
  formatCadence,
  formatDurationUntil,
  nextDiscoveryISO,
  nextTuesdayISO,
  nextUtcMidnight,
  shortDate,
} from '../lib/cadence'
import './ResearchCadenceStrip.css'

const NONE = '—'

/** Smallest ISO string (earliest date) over a list, or null when empty. */
function earliest(dates: string[]): string | null {
  if (dates.length === 0) return null
  return dates.reduce((a, b) => (a <= b ? a : b))
}

interface CadenceCellProps {
  icon: React.ReactNode
  label: string
  value: string
  sub: string
  tooltip: string
  dim?: boolean
}

/** Presentational shell shared by the day-granularity and countdown cells. */
function CadenceCell({ icon, label, value, sub, tooltip, dim }: CadenceCellProps) {
  return (
    <div className={`cadence-cell${dim ? ' cadence-cell--dim' : ''}`} title={tooltip}>
      <div className="cadence-cell-label">
        {icon}
        {label}
      </div>
      <div className="cadence-cell-value num">{value}</div>
      <div className="cadence-cell-sub">{sub}</div>
    </div>
  )
}

interface TimerCellProps extends Omit<CadenceCellProps, 'value' | 'sub'> {
  targetISO: string | null
  today: string
}

/** Day-granularity countdown to a calendar-day event. */
function TimerCell({ targetISO, today, ...rest }: TimerCellProps) {
  const hasTarget = targetISO !== null
  return (
    <CadenceCell
      {...rest}
      value={hasTarget ? formatCadence(daysUntil(targetISO, today)) : NONE}
      sub={hasTarget ? shortDate(targetISO) : 'no names incubating'}
    />
  )
}

/**
 * Research Cadence timers — a read-only hero strip on the research book view.
 * Every countdown is DERIVED client-side from the already-fetched conviction
 * roster + the ET clock (no "next event" endpoint exists). Day-granularity;
 * the shared 30s roster refetch re-renders the countdowns.
 */
export function ResearchCadenceStrip({ accountId }: { accountId: number }) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['conviction-roster', accountId],
    queryFn: () => convictionApi.roster(accountId),
    retry: 1,
    refetchInterval: 30000,
  })

  // Ticks every 30s so the LLM-budget countdown moves; also keeps `today`
  // fresh across an ET midnight without a page reload.
  const now = useNow(30_000)
  const today = etTodayISO(now)
  const budgetResetAt = nextUtcMidnight(now)

  const incubating = (data?.candidates ?? []).filter((c) => c.state === 'incubating')
  const confirmN = data?.confirm_n ?? 5
  const timeoutDays = data?.incubation_timeout_days ?? 21

  const nextEntry = earliest(
    incubating.filter((c) => !!c.flag_date).map((c) => addBusinessDays(c.flag_date, confirmN)),
  )
  const nextStaleOut = earliest(
    incubating
      .map((c) => c.stale_out_date)
      .filter((d): d is string => d !== null && d !== undefined),
  )

  return (
    <div className="card cadence-strip">
      <div className="card-header">
        <h3 className="card-title">
          <CalendarClock size={18} className="icon" aria-hidden="true" />
          Research cadence
        </h3>
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : isError ? (
        <div className="empty-state">
          <div className="empty-icon">
            <CalendarClock size={28} aria-hidden="true" />
          </div>
          <div className="empty-text">cadence unavailable</div>
        </div>
      ) : (
        <div className="cadence-grid">
          <TimerCell
            icon={<CalendarSearch size={13} aria-hidden="true" />}
            label="Next discovery"
            targetISO={nextDiscoveryISO(today)}
            today={today}
            tooltip="New candidates are flagged on the first trading day of each month (holiday-blind estimate)."
          />
          <TimerCell
            icon={<RefreshCw size={13} aria-hidden="true" />}
            label="Next bench re-score"
            targetISO={nextTuesdayISO(today)}
            today={today}
            tooltip="Weekly — the Monday re-score is applied on the following trading session (Tue). Holiday-blind."
          />
          <TimerCell
            icon={<Sprout size={13} aria-hidden="true" />}
            label="Entries eligible ~"
            targetISO={nextEntry}
            today={today}
            tooltip={`Incubating names become buyable ~${confirmN} trading days after flag; holiday-blind estimate.`}
          />
          <TimerCell
            icon={<Timer size={13} aria-hidden="true" />}
            label="Soonest stale-out"
            targetISO={nextStaleOut}
            today={today}
            tooltip={`Incubation times out ${timeoutDays} calendar days after flag.`}
          />
          {/* The only sub-daily event in the strip — hour/minute granularity,
              not "today"/"tomorrow". Its sub-label carries the reset's ET wall
              clock so the countdown is checkable against the operator's clock. */}
          <CadenceCell
            icon={<Zap size={13} aria-hidden="true" />}
            label="LLM budget resets"
            value={formatDurationUntil(budgetResetAt.getTime() - now.getTime())}
            sub={`${etWallClock(budgetResetAt)} · ${shortDate(etTodayISO(budgetResetAt))}`}
            tooltip="News-scoring budget resets daily at 00:00 UTC."
            dim
          />
        </div>
      )}
    </div>
  )
}
